'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from 'utils/cn'
import { useScrollPosition } from 'utils/hooks/useScrollPosition'
import { MainMenu } from '../Navigation/MainMenu'
import { MegaMenu } from '../Navigation/MegaMenu'
import { QuickAccessMenu } from '../Navigation/QuickAccessMenu'
import { ProgressbarScrolling } from '../Progressbar/ProgressbarScroll'

export const headerId = 'main-header'
export const headerNavId = 'main-header-nav'
export const headerProgressbarId = 'main-header-progressbar'

type DebounceFunction = (
  func: (param: number) => void,
  wait: number,
) => (...args: unknown[]) => void

const SHOW_THRESHOLD = 300
const HIDE_THRESHOLD = 500

// Debounce function to prevent lag when scrolling in the 5% range
const debounce: DebounceFunction = (func, wait) => {
  let timeout: NodeJS.Timeout | null = null
  return (...args: unknown[]) => {
    if (timeout) clearTimeout(timeout)
    // @ts-expect-error -- will be a corrent input
    timeout = setTimeout(() => func(...args), wait)
  }
}

export const Header = ({
  customMobileContent,
  showSubmenu = true,
  locale,
}: {
  showSubmenu?: boolean
  customMobileContent?: React.ReactNode
  customContent?: React.ReactNode
  locale: string
}) => {
  const scrollPosition = useScrollPosition()
  const [isVisible, setIsVisible] = useState(true)
  const disableProgressScrollbar = true

  const debouncedCallback = useMemo(
    () =>
      debounce((position: number) => {
        setIsVisible((prev: boolean) => {
          if (prev && position > HIDE_THRESHOLD) {
            return false
          } else if (!prev && position < SHOW_THRESHOLD) {
            return true
          }

          return prev
        })
        // delay here to prevent lag when scrolling
      }, 10),
    [],
  )

  const updateVisibility = useCallback(
    (position: number) => {
      debouncedCallback(position)
    },
    [debouncedCallback],
  )

  useEffect(() => {
    updateVisibility(scrollPosition)
  }, [scrollPosition, updateVisibility])

  return (
    <header
      className='bg-surface-primary top-0 z-40 flex flex-col items-center'
      id={headerId}
    >
      <QuickAccessMenu
        customMobileContent={customMobileContent}
        locale={locale}
      />

      <hr className='border-b-surface-disabled w-full border-0 border-b' />

      {showSubmenu ? (
        <div
          className={cn(
            'hidden w-full max-w-[calc(theme(screens.content)+120px)] items-center justify-between gap-2 px-4 transition-all duration-150 ease-in-out md:px-[60px] lg:flex',
            isVisible
              ? 'h-[70px] opacity-100'
              : 'h-0 overflow-hidden opacity-0',
          )}
          id={headerNavId}
        >
          <MainMenu locale={locale} />
          <MegaMenu locale={locale} />
        </div>
      ) : null}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fine for now */}
      {!disableProgressScrollbar ? (
        <ProgressbarScrolling id={headerProgressbarId} />
      ) : null}
    </header>
  )
}
